import { AuctionStates, ProductStates } from "../../../utils/misc";
import { buildProductSchema } from "@/common/utils/json-ld.utils";

const POSTALS_STANDARD = ["standard", "custom"];
const POSTAL_REGISTERED = "registered";
const CATEGORY_ART = "kunst";

/**
 * @typedef {import("../../../Apollo/schema").Product} Product
 * @typedef {import("../../../Apollo/schema").ProductBySlugQuery['productBySlug']} ProductBySlug
 * @typedef {import("../../../Apollo/schema").Merchant} Member
 * @typedef {import("type-fest").PartialDeep} PartialDeep
 */

const LABEL_MAP = {
  // ["ACCEPTED,RESERVED"]: {
  //   title: "common:auction-state-reserved",
  //   className: "bg-[#EADEE0] text-[#5A272E]",
  //   priority: true,
  // },
  sold: {
    title: "common:auction-state-already-sold",
    className: "bg-[#FFF7F7] text-[#E26F77]",
    priority: true,
  },
  ["ACCEPTED,COMING_SOON"]: {
    title: "common:common-coming-soon",
    className: "bg-[#282828] text-[#FFFFFF]",
  },
};

const ProductUtils = {
  /**
   * Build the JSON-LD Product schema for a PDP.
   *
   * Implementation now lives in `@/common/utils/json-ld.utils` so it
   * sits next to the other GEO schema builders and is easier to
   * maintain. Behavior is intentionally a superset of the previous
   * implementation: it still emits price, availability, condition,
   * images, brand and description, plus the new enrichments
   * (category, material, color, designer/style/dimensions via
   * additionalProperty, brand-always-on, seller=Whoppah Org).
   *
   * Retail-only fields (sku/GTIN/MPN, priceValidUntil,
   * hasMerchantReturnPolicy, shippingDetails, aggregateRating)
   * are deliberately omitted — those don't model a one-off
   * secondhand marketplace cleanly and risk invalid Rich Results.
   */

  /**
   * Get rich snippet (share) of the product.
   *
   * @param {Product} [product]
   * @param {string} [locale]
   * @param {string} [defaultLocale]
   */
  getRichSnippet: (
    product = undefined,
    locale = undefined,
    defaultLocale = undefined
  ) => {
    return buildProductSchema(product, locale, defaultLocale);
  },
  /**
   * Get product price info.
   *
   * @param {Product | ProductBySlug} product
   */
  getPriceInfo: product => {
    const buyNowPrice = product.auction?.buy_now_price;
    const buyNowPriceAmount = buyNowPrice?.amount ?? 0;

    const originalPrice = product.original_price;
    const originalPriceAmount = originalPrice?.amount ?? buyNowPriceAmount;

    const previousPrice =
      buyNowPriceAmount < originalPriceAmount ? originalPrice : undefined;

    const discountPercentage = Math.round(
      ((previousPrice?.amount - buyNowPrice?.amount) / previousPrice?.amount) *
        100
    );

    const isLoweredInPrice =
      (buyNowPrice?.amount ?? 0) < (previousPrice?.amount ?? 0);

    const soldPrice = product?.auction?.sold_at;

    const bidPrice =
      product?.auction?.allow_bid && product?.auction?.minimum_bid
        ? product?.auction?.minimum_bid
        : undefined;

    return {
      price: buyNowPrice,
      previousPrice,
      soldPrice,
      bidPrice,
      discountPercentage,
      isLoweredInPrice,
    };
  },
  /**
   * Get label based on product state / auction state.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   */
  getLabel: product => {
    const sold = product?.auction?.sold_at ? LABEL_MAP.sold : undefined;

    const productState = product.state;
    const auctionState = product.auction?.state;

    const key = `${productState},${auctionState}`;
    return sold ?? LABEL_MAP[key];
  },

  /**
   * Is the product sold based on auction and product state.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   *
   * @returns {boolean}
   *
   */
  isSold: product => {
    return Boolean(product?.auction?.sold_at);
  },

  /**
   * Is the product reserved based on auction and product state.
   *
   * @param {Product} product
   *
   * @returns {boolean}
   */
  isReserved: product => {
    const isSold = Boolean(product?.auction?.sold_at);

    return !isSold && Boolean(product?.auction?.state === "RESERVED");
  },

  /**
   * Bid only product state.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   *
   * @returns {boolean}
   */
  isBidOnly: product => {
    return (
      product?.auction?.state === AuctionStates.BID_ONLY &&
      product?.state === ProductStates.ACCEPTED
    );
  },

  /**
   * Allow product bid.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   *
   * @returns {boolean}
   */
  allowBid: product => {
    return product?.auction?.allow_bid ?? false;
  },

  /**
   * Is coming soon product state.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   *
   * @returns {boolean}
   */
  isComingSoon: product => {
    return (
      product?.auction?.state === AuctionStates.COMING_SOON &&
      product?.state === ProductStates.ACCEPTED
    );
  },

  /**
   * Get info if user is an product owner.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   * @param {Merchant} merchant
   *
   * @returns {boolean}
   */
  isProductOwner: (product, merchant) => {
    const productMerchantId = product.merchant?.id;
    const merchantId = merchant?.id;

    return productMerchantId === merchantId;
  },

  /**
   * Is the product of category ART.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   *
   * @returns {boolean}
   */
  isArt: product => {
    const categories = product.categories.filter(cat => cat.level === 0);
    const category = categories[0]?.slug;

    return category === CATEGORY_ART;
  },

  /**
   * Is the product/ad Expiring.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   *
   * @returns {boolean}
   */
  isExpiring: product => {
    return Boolean(product?.status === "EXPIRING");
  },

  isActive: product => {
    return Boolean(product?.status === "ACTIVE");
  },

  /**
   * Loved product label.
   *
   * @param {PartialDeep<Product, { recurseIntoArrays: true }> | null} product
   *
   * @returns {boolean}
   */
  isLovedByWhoppah: product => {
    return product.attributes.reduce((acc, curr) => {
      if (
        curr.__typename.toLocaleUpperCase() === "LABEL" &&
        curr?.slug === "whoppah-loves"
      ) {
        acc = true;
      }
      return acc;
    }, null);
  },

  hasDelivery: product => {
    return product?.delivery_method === "DELIVERY";
  },

  hasDeliveryOrPickupDelivery: product => {
    return product?.delivery_method === "PICKUP_DELIVERY";
  },

  hasOnlyPickupDelivery: product => {
    return product?.delivery_method === "PICKUP";
  },

  hasCourierShipping: product => {
    return product?.shipping_method?.slug === "courier";
  },

  hasPostalShipping: product => {
    return product?.shipping_method?.slug === "postal";
  },

  hasStandardPostalShipping: product => {
    return POSTALS_STANDARD.includes(product?.shipping_method?.slug);
  },

  hasRegisteredPostalShipping: product => {
    return product?.shipping_method?.slug === POSTAL_REGISTERED;
  },

  isInShowroom: product => {
    return Boolean(product?.showroomModel);
  },

  hasGroundFloorDelivery: product => {
    return Boolean(product?.ground_floor_only_delivery);
  },

  hasMobileARModel: product => {
    const android_arobject =
      product?.android_arobject ?? product?.product_type?.android_arobject;
    const ios_arobject =
      product?.ios_arobject ?? product?.product_type?.ios_arobject;

    return android_arobject || ios_arobject;
  },
};

export default ProductUtils;
